import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useWebSocketConnection, type WebSocketMessage } from '../hooks/useWebSocketConnection';
import { useWebsocketFeature } from '../hooks/useWebsocketFeature';
import { logError } from '@zg-rentals/log-error';

type WebSocketContextType = {
  hadErrorSinceLastOpen: boolean;
  isConnecting: boolean;
  isWebSocketEnabledAndReady: boolean;
  lastMessage: WebSocketMessage | null;
  authenticatedUserToken: string | null;
  subscribeToMessages: (
    callback: (message: WebSocketMessage) => void,
    options?: {
      conversationId?: string; // Optional: subscribe to specific conversation only
    },
  ) => () => void;
};

const WebSocketContext = createContext<WebSocketContextType | null>(null);

export const useWebSocketContext = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within a WebSocketContextProvider');
  }
  return context;
};

type WebSocketContextProviderProps = {
  children: React.ReactNode;
  topic: 'renter';
};

export const WebSocketContextProvider = ({ children, topic = 'renter' }: WebSocketContextProviderProps) => {
  const isWebSocketEnabled = useWebsocketFeature();
  const messageCallbacksRef = useRef<Set<(message: WebSocketMessage) => void>>(new Set());
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);

  const subscribeToMessages = useCallback(
    (
      callback: (message: WebSocketMessage) => void,
      options?: {
        conversationId?: string;
      },
    ) => {
      messageCallbacksRef.current.add(callback);

      if (options?.conversationId) {
        setCurrentConversationId(options.conversationId);
      }

      // Return unsubscribe function
      return () => {
        messageCallbacksRef.current.delete(callback);

        // Clear conversation ID when unsubscribing
        if (options?.conversationId) {
          setCurrentConversationId(null);
        }
      };
    },
    [],
  );

  const handleWebSocketMessage = useCallback((message: WebSocketMessage) => {
    messageCallbacksRef.current.forEach((callback) => {
      try {
        callback(message);
      } catch (error) {
        logError({ error, context: 'Error in WebSocket message callback' });
      }
    });
  }, []);

  const topicSuffixes = useMemo(() => {
    const topics = [`|inbox:${topic}`];

    if (currentConversationId) {
      topics.push(`|conversation:${currentConversationId}:${topic}`);
    }

    return topics;
  }, [currentConversationId, topic]);

  const webSocketResult = useWebSocketConnection({
    onNewMessage: handleWebSocketMessage,
    isEnabled: isWebSocketEnabled,
    topicSuffixes,
  });

  const contextValue: WebSocketContextType = {
    hadErrorSinceLastOpen: webSocketResult?.hadErrorSinceLastOpen || false,
    isConnecting: webSocketResult?.isConnecting || false,
    isWebSocketEnabledAndReady: webSocketResult?.isWebSocketEnabledAndReady || false,
    lastMessage: webSocketResult?.lastMessage || null,
    authenticatedUserToken: webSocketResult?.authenticatedUserToken || null,
    subscribeToMessages,
  };

  return <WebSocketContext.Provider value={contextValue}>{children}</WebSocketContext.Provider>;
};
